import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateInputLabelProps,
} from '../../theme/harmonateStyles';

const ForgotPasswordDialog = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      // Handle both JSON and plain text responses
      let responseData;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = { message: await response.text() };
      }
      
      if (response.ok) {
        setEmailSent(true);
        toast.success('Password reset link has been sent to your email');
      } else {
        throw new Error(responseData.message || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.message || 'An error occurred while processing your request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setEmailSent(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={harmonateDialogPaperProps}
    >
      <DialogTitle sx={harmonateDialogTitleSx}>Forgot Password</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          {emailSent ? (
            <Alert
              severity="success"
              sx={{
                borderRadius: ht.buttonRadius,
                bgcolor: 'rgba(2, 107, 29, 0.1)',
                color: ht.green,
                border: `1px solid ${ht.green}`,
                '& .MuiAlert-icon': { color: ht.green },
              }}
            >
              If an account exists with {email}, you will receive a password reset link shortly.
            </Alert>
          ) : (
            <>
              <Typography variant="body1" sx={{ color: ht.text }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                id="email"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                InputLabelProps={harmonateInputLabelProps}
                sx={{
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: loading ? ht.drawerBg : ht.listBg,
                  },
                }}
              />
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: ht.buttonRadius,
                    backgroundColor: 'rgba(239, 63, 63, 0.1)',
                    color: ht.red,
                    border: `1px solid ${ht.red}`,
                    '& .MuiAlert-icon': { color: ht.red },
                  }}
                >
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={handleClose} disabled={loading} sx={harmonateCancelButtonSx}>
            {emailSent ? 'Close' : 'Cancel'}
          </Button>
          {!emailSent && (
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} sx={{ color: ht.white }} /> : null}
              sx={harmonateProceedButtonSx}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
